import { fetchApiGateway } from "@nova/fetch-api-gateway"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { Dispatch, SetStateAction, useEffect } from "react"
import {
    clearLocalChanges,
    fetchProfile,
    selectDefaultWorkspaceId,
    selectEmail,
    selectFirstName,
    selectHasUnsavedChanges,
    selectIsFetchingProfile,
    selectIsSavingChanges,
    selectLastName,
    selectOrganizationName,
    selectPhone,
    selectTimezoneId,
    selectTitle,
    setDefaultWorkspaceId,
    setFirstName,
    setIsSavingChanges,
    setLastName,
    setOrganizationName,
    setPhone,
    setTimezoneId,
    setTitle,
} from "./slice"
import { useLoadingText } from "../../../useLoadingText"
import { ButtonPrimary } from "@nova/component-btn"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { NovaSpinner } from "@nova/component-nova-spinner"
import { ProfilePhoto } from "./ProfilePhoto"
import { Input } from "@nova/ds-component-input"
import { Select } from "@nova/component-select"
import {
    getAllWorkspaces,
    selectActiveWorkspaceId,
    selectAllWorkspaces,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { possibleTimezones, TimezoneId } from "@nova/constants"
import { formatPartialPhoneNumberInput } from "@nova/util-strings"
import { listMembersForWorkspace } from "features/entity-caching/members/UserManagementSlice"
import {
    getGenericWorkspacePage,
    getGenericWorkspacePageHeader,
    getGenericSaveableWorkspacePageContent,
} from "panels/routing/WorkspaceRoutes"

export const ProfileSettingsPage = getGenericWorkspacePage({
    Header: getGenericWorkspacePageHeader({ title: "Profile" }),
    Content: getGenericSaveableWorkspacePageContent({
        Content: ProfileSettingsPageContent,
        Footer: ProfileSettingsPageFooter,
    }),
})

function ProfileSettingsPageContent({
    setHasUnsavedChanges,
}: {
    setHasUnsavedChanges?: Dispatch<SetStateAction<boolean>>
}) {
    const dispatch = useAppDispatch()
    const firstName = useAppSelector(selectFirstName)
    const lastName = useAppSelector(selectLastName)
    const email = useAppSelector(selectEmail)
    const phone = useAppSelector(selectPhone)
    const orgName = useAppSelector(selectOrganizationName)
    const title = useAppSelector(selectTitle)
    const defaultWorkspaceId = useAppSelector(selectDefaultWorkspaceId)
    const timezoneId = useAppSelector(selectTimezoneId)
    const isFetchingProfile = useAppSelector(selectIsFetchingProfile)
    const hasUnsavedChanges = useAppSelector(selectHasUnsavedChanges)
    const allWorkspaces = useAppSelector(selectAllWorkspaces())
    useEffect(() => {
        setHasUnsavedChanges?.(hasUnsavedChanges)
    }, [hasUnsavedChanges, setHasUnsavedChanges])
    useEffect(() => {
        dispatch(fetchProfile())
    }, [dispatch])
    if (isFetchingProfile)
        return (
            <div className="w-full h-full flex justify-center items-center">
                <NovaSpinner width={24} height={24} />
            </div>
        )
    return (
        <div className="flex flex-col gap-8">
            <div className="flex">
                <small className="w-[168px] min-w-[168px] xs">PUBLIC INFO</small>
                <div className="flex flex-col gap-4 w-full max-w-[548px]">
                    <div className="w-full flex justify-between items-center">
                        <div className="w-[140px] aspect-square rounded-2xl overflow-hidden">
                            <ProfilePhoto self allowUpload />
                        </div>
                        {/* <div className="text-gray-300">View Public Profile</div> */}
                    </div>
                    <div className="flex flex-col gap-2 w-full">
                        <div className="flex gap-12 w-full">
                            <Input
                                label="First Name *"
                                value={firstName}
                                onChange={(e) => dispatch(setFirstName(e.target.value))}
                            />
                            <Input
                                label="Last Name *"
                                value={lastName}
                                onChange={(e) => dispatch(setLastName(e.target.value))}
                            />
                        </div>
                        <div className="flex gap-12">
                            <Input label="Account Email *" value={email} disabled />
                            <Input
                                label="Phone Number"
                                value={phone}
                                onChange={(e) =>
                                    dispatch(
                                        setPhone(formatPartialPhoneNumberInput(e.target.value))
                                    )
                                }
                            />
                        </div>
                        <div className="flex gap-12">
                            <Input
                                label="Organization Name"
                                value={orgName}
                                onChange={(e) => dispatch(setOrganizationName(e.target.value))}
                            />
                            <Input
                                label="Title"
                                value={title}
                                onChange={(e) => dispatch(setTitle(e.target.value))}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className="w-full h-px bg-gray-700" />
            <div className="flex">
                <small className="w-[168px] min-w-[168px] xs">PREFERENCES</small>
                <div className="flex flex-col gap-4 w-full max-w-[380px]">
                    <div className="flex flex-col gap-2 w-full">
                        <div className="text-gray-100">Default Workspace</div>
                        <Select<string>
                            options={allWorkspaces.map((workspace) => ({
                                label: workspace.name,
                                value: workspace.id,
                            }))}
                            onChange={(workspaceId) => dispatch(setDefaultWorkspaceId(workspaceId))}
                            value={defaultWorkspaceId}
                            placeholder=""
                            className="!w-full"
                        />
                    </div>
                    <div className="flex flex-col gap-2 w-full">
                        <div className="text-gray-100">Time Zone</div>
                        <Select<TimezoneId | null>
                            options={possibleTimezones.map((timezone) => ({
                                label: timezone.name,
                                value: timezone.id,
                            }))}
                            onChange={(id) => dispatch(setTimezoneId(id))}
                            value={timezoneId}
                            placeholder="Not Selected"
                            className="!w-full"
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}

function ProfileSettingsPageFooter() {
    const dispatch = useAppDispatch()
    const isFetchingProfile = useAppSelector(selectIsFetchingProfile)
    const hasUnsavedChanges = useAppSelector(selectHasUnsavedChanges)
    const isSavingChanges = useAppSelector(selectIsSavingChanges)
    const savingText = useLoadingText(isSavingChanges, "Saving changes")

    const firstName = useAppSelector(selectFirstName)
    const lastName = useAppSelector(selectLastName)
    const email = useAppSelector(selectEmail)
    const phoneNumber = useAppSelector(selectPhone)
    const organizationName = useAppSelector(selectOrganizationName)
    const title = useAppSelector(selectTitle)
    const defaultWorkspaceId = useAppSelector(selectDefaultWorkspaceId)
    const timezoneId = useAppSelector(selectTimezoneId)
    const workspaceId = useAppSelector(selectActiveWorkspaceId())!

    return (
        <div className="w-full h-full flex items-center gap-2">
            <ButtonPrimary
                title="Save Changes"
                disabled={!hasUnsavedChanges || isSavingChanges || isFetchingProfile}
                onClick={async () => {
                    dispatch(setIsSavingChanges(true))
                    await fetchApiGateway("patch /me/profile", {
                        body: {
                            givenName: firstName,
                            familyName: lastName,
                            email,
                            phoneNumber,
                            organizationName,
                            title,
                            defaultWorkspaceId,
                            timezoneId: timezoneId ?? undefined,
                        },
                    })
                    dispatch(getAllWorkspaces())
                    dispatch(listMembersForWorkspace({ workspaceId }))
                    dispatch(clearLocalChanges())
                    dispatch(setIsSavingChanges(false))
                    dispatch(
                        setGlobalNotificationContext({
                            message: "Changes have been saved.",
                            status: GlobalNotificationStatus.success,
                            timeoutInMs: 3000,
                        })
                    )
                }}
            />
            {(hasUnsavedChanges || isSavingChanges) && (
                <small className="text-gray-300">
                    {isSavingChanges ? savingText : "* Unsaved changes"}
                </small>
            )}
        </div>
    )
}
